/**
 * Карточка счёта и акта — §2.
 *
 * Позиции правятся ПОСЛЕ создания счёта и живут независимо от заказов (§2.1):
 * можно добавить доставку, поправить формулировку или цену, и на исходные
 * заказы это не повлияет.
 *
 * Номер тоже правится (§2.2) — это единственный способ привести нумерацию
 * в порядок, если её начали не с того числа.
 */

import { DocumentPreview } from '../components/DocumentPreview.js';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CheckCircle2, Download, FileText, ListChecks, Mail, Plus, Printer, Send, Trash2, Wallet, X } from 'lucide-react';
import {
  buildDocument,
  deliveryLast,
  composeInvoiceLetter,
  renderDocumentPage,

  formatDate,
  formatDateDots,
  formatKop,
  nameIndex,
  retitleInvoiceItems,
  detectInvoicePeople,
  type InvoiceableOrder,

  rublesToKop,
  type DocumentKind,
  type Facsimile,
  type Legal,
  type Performer,  invoiceNumberText,
} from '@otc/shared';

import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, documentUrl, downloadFile } from '../api/client.js';
import { invalidateAfterCommit, type ApiInvoice, type Bootstrap } from '../api/queries.js';
import { Button } from '../components/ui.js';
import { CardWindow } from '../components/CardWindow.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { MailComposeDialog } from '../components/MailComposeDialog.js';
import { STATUS_COLORS } from '../theme.js';
import { ActionPill, Pilule } from '../components/Pills.js';
import { prefersDownload } from '../components/isNarrow.js';
import { printHtml } from '../components/printHtml.js';

interface EditableItem {
  id: string;
  name: string;
  qty: number;
  unit: string;
  priceKop: number;
}

export function InvoiceDetailModal({
  invoice,
  bootstrap,
  onClose,
  onChanged,
  onDeleted,
}: {
  invoice: ApiInvoice;
  bootstrap: Bootstrap;
  onClose: () => void;
  /**
   * Счёт изменился — перечитать список и сказать об этом.
   *
   * `держатьОткрытым` — не закрывать карточку счёта. Нужно отправке письма:
   * она ничего в счёте не меняет, а закрытие выбрасывало человека в список
   * ровно в тот момент, когда он собирался проверить отправленное.
   */
  onChanged: (message: string, держатьОткрытым?: boolean) => void;
  onDeleted: (message: string) => void;
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<DocumentKind>('schet');

  /*
   * Узкий экран определяется ОДИН РАЗ при открытии окна: поворачивать телефон
   * с открытым счётом никто не станет, а пересчёт на каждую отрисовку стоил бы
   * обращения к раскладке страницы.
   */
  const [narrow] = useState(prefersDownload);
  const [busy, setBusy] = useState<'pdf' | 'docx' | null>(null);

  /*
   * Правка счёта меняет и ЗАКАЗЫ: у них проставляется или снимается номер
   * счёта. Экран заказов держит свой список в кеше и сам его не перечитывает,
   * поэтому после удаления счёта номера оставались на месте до перезагрузки
   * страницы. Сброс делается здесь, а не у вызывающего: окно открывается и из
   * «Документов», и из «Заказов», и чинить пришлось бы оба.
   */
  const queryClient = useQueryClient();
  /** Окно подтверждения перед отправкой счёта клинике. */
  const [sending, setSending] = useState(false);
  const [mailDocuments, setMailDocuments] = useState<('schet' | 'akt')[]>(['schet', 'akt']);
  const mailLabel=mailDocuments.length===1?(mailDocuments[0]==='akt'?'Акт':'Счёт'):'Счёт и акт';
  const mailLetter=composeInvoiceLetter({number:invoice.number,date:formatDateDots(invoice.date),template:bootstrap.settings?.templates?.invoiceTemplate});
  mailLetter.subject=`${mailLabel} № ${invoiceNumberText(invoice.number, invoice.numberSuffix)} от ${formatDateDots(invoice.date)}`;
  if(mailDocuments.length===1)mailLetter.body=mailLetter.body.replace(/счёт и акт выполненных работ/gi,mailDocuments[0]==='akt'?'акт выполненных работ':'счёт');

  const grab = async (format: 'pdf' | 'docx') => {
    setBusy(format);
    try {
      await downloadFile(documentUrl(invoice.id, tab, format), `${tab === 'akt' ? 'Акт' : 'Счёт'}.${format}`);
    } catch (error) {
      onChanged(error instanceof Error ? error.message : 'Не удалось получить документ');
    } finally {
      setBusy(null);
    }
  };
  const [editMode, setEditMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [number, setNumber] = useState(invoice.number);
  /** Что подтверждаем: `true` — отметить оплаченным, `false` — снять. */
  const [confirmPaid, setConfirmPaid] = useState<boolean | null>(null);
  const [payBusy, setPayBusy] = useState(false);
  const [items, setRawItems] = useState<EditableItem[]>(() =>
    deliveryLast(invoice.items).map((item) => ({
      id: item.id, name: item.name, qty: item.qty, unit: item.unit, priceKop: item.priceKop,
    })),
  );

  const setItems = (next: Parameters<typeof setRawItems>[0]) => setRawItems(current => deliveryLast(typeof next === 'function' ? next(current) : next));
  /*
   * ФАМИЛИИ В СТРОКАХ УЖЕ ВЫСТАВЛЕННОГО СЧЁТА.
   *
   * При выставлении их включают в окне создания, но клиника просит дописать
   * врача и потом — по готовому счёту, который уже лежит в программе. Руками
   * это означало переписать каждую строку: в счёте их бывает три десятка.
   *
   * Строка счёта не помнит своего заказа (§2.1), поэтому фамилии берутся
   * из заказов счёта — их отдаёт отдельный запрос, и только при правке:
   * тому, кто просто смотрит счёт, они не нужны.
   */
  const merged = legalMerged(bootstrap, invoice);
  const catalog = useMemo(() => nameIndex(bootstrap.constructions), [bootstrap.constructions]);
  const ordersQuery = useQuery({
    queryKey: ['invoice-orders', invoice.id],
    queryFn: () => api.get<{ orders: InvoiceableOrder[] }>(`/api/invoices/${invoice.id}/orders`),
    enabled: editMode && !merged,
    staleTime: Infinity,
  });
  const invoiceOrders = ordersQuery.data?.orders;

  /** Как фамилии стоят в этом счёте. `null` — заказы ещё не пришли. */
  const [people, setPeople] = useState<{ patient: boolean; doctor: boolean } | null>(null);

  const clinic = bootstrap.clinics.find((c) => c.id === invoice.clinicId);
  const legal = invoice.documentRequisites?.legal ?? clinic?.legals.find((l) => l.id === invoice.legalId);
  const performer = invoice.documentRequisites?.performer ?? bootstrap.settings?.performer as unknown as Performer | undefined;

  /*
   * Положение выключателей читается по САМИМ СТРОКАМ, а не по карточке
   * клиники: выключатели там могли поменять уже после выставления, и тогда
   * галочка показывала бы врача там, где в строках его нет.
   */
  useEffect(() => {
    if (!invoiceOrders || people) return;
    setPeople(
      detectInvoicePeople(items, invoiceOrders, catalog, {
        patient: legal?.invoicePatient ?? true,
        doctor: legal?.invoiceDoctor ?? false,
      }),
    );
    // Считается один раз на пришедшие заказы; правки строк это не сбрасывает.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceOrders]);

  /**
   * Переключение фамилии переписывает названия машинных строк.
   *
   * Только их: строка, вписанная руками — доставка, скидка, своя
   * формулировка, — остаётся как есть, вместе с ценой и количеством.
   */
  const switchPeople = (next: { patient: boolean; doctor: boolean }) => {
    if (!invoiceOrders) return;
    setPeople(next);
    setItems(retitleInvoiceItems(items, invoiceOrders, catalog, next));
  };

  /*
   * Подпись и печать — с сервера и только для вошедших: в код программы их
   * не кладут, его скачивает любой, кто открыл сайт.
   */
  const facsimileQuery = useQuery({
    queryKey: ['facsimile'],
    queryFn: () => api.get<{ available: boolean } & Partial<Facsimile>>('/api/facsimile'),
    staleTime: Infinity,
  });
  const facsimileFiles: Facsimile | null =
    facsimileQuery.data?.signature && facsimileQuery.data.stamp
      ? { signature: facsimileQuery.data.signature, stamp: facsimileQuery.data.stamp }
      : null;

  /** Факсимиле по документам. Сохраняется сразу, без «Сохранить правки». */
  const [facsimileOn, setFacsimileOn] = useState({ schet: invoice.facsimileSchet, akt: invoice.facsimileAkt });
  const [facsimileBusy, setFacsimileBusy] = useState(false);
  const toggleFacsimile = async () => {
    const next = !facsimileOn[tab];
    setFacsimileBusy(true);
    setError(null);
    try {
      await api.post(`/api/invoices/${invoice.id}/facsimile`, { kind: tab, on: next });
      setFacsimileOn((current) => ({ ...current, [tab]: next }));
      invalidateAfterCommit(queryClient);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось переключить подпись и печать');
    } finally {
      setFacsimileBusy(false);
    }
  };

  const totalKop = items.reduce((sum, item) => sum + Math.round(item.priceKop * item.qty), 0);
  const changed =
    number !== invoice.number ||
    JSON.stringify(items) !==
      JSON.stringify(
        invoice.items.map((i) => ({ id: i.id, name: i.name, qty: i.qty, unit: i.unit, priceKop: i.priceKop })),
      );

  const model =
    performer && legal
      ? buildDocument({
          kind: tab,
          performer,
          legal: legal as unknown as Legal,
          clinicName: invoice.documentRequisites?.clinicName ?? clinic?.name ?? '',
          number,
          numberSuffix: invoice.numberSuffix,
          date: invoice.date,
          items: items.map((i) => ({ name: i.name, qty: i.qty, unit: i.unit, priceKop: i.priceKop })),
          facsimile: facsimileOn[tab] ? facsimileFiles : null,
        })
      : null;

  /**
   * Оплата счёта целиком.
   *
   * Правки позиций сюда не примешиваются: это разные действия, и человек,
   * отмечающий оплату, не ждёт, что заодно сохранятся его незаконченные
   * правки в таблице. Сервер меняет заказы, а счёт подтягивает статус сам.
   */
  const setPaid = async (paid: boolean) => {
    setPayBusy(true);
    setError(null);
    try {
      await api.post(`/api/invoices/${invoice.id}/paid`, { paid });
      invalidateAfterCommit(queryClient);
      setConfirmPaid(null);
      onChanged(
        paid
          ? `Счёт №${invoiceNumberText(invoice.number, invoice.numberSuffix)} и его заказы отмечены оплаченными`
          : `С счёта №${invoiceNumberText(invoice.number, invoice.numberSuffix)} снята отметка оплаты`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отметить оплату');
    } finally {
      setPayBusy(false);
    }
  };

  const persist = async (extra: Partial<{ status: ApiInvoice['status'] }> = {}) => {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/api/invoices/${invoice.id}`, {
        version: invoice.version,
        number,
        items: items.map((item) => ({
          name: item.name, qty: item.qty, unit: item.unit, priceKop: item.priceKop,
        })),
        ...extra,
      });
      invalidateAfterCommit(queryClient);
      /* Молча: окно закрывается, и правка видна в самом списке. */
      onChanged('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      const result = await api.del<{ releasedOrders: number }>(`/api/invoices/${invoice.id}`);
      invalidateAfterCommit(queryClient);
      onDeleted(`Счёт №${invoiceNumberText(invoice.number, invoice.numberSuffix)} удалён, заказов освобождено: ${result.releasedOrders}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить');
      setSaving(false);
    }
  };

  return (
    /*
      ПОДВИЖНОЕ ОКНО, а не модальное (решение владельца 28.09.2026).

      Было модальным: затемнение на весь экран, список счетов под ним мёртв.
      Теперь карточка счёта ведёт себя как карточка заказа, врача и клиники —
      её таскают за шапку, тянут за край, а список под ней остаётся рабочим:
      видно, какой счёт открыт, и можно листать соседние, не закрывая окно.

      Своё место запоминается отдельным ключом: карточка счёта шире карточки
      врача, и сводить их к одной ширине незачем.
    */
    <CardWindow
      ключ="otc.invoiceWindow"
      ширина={1000}
      title={`Счёт и акт №${invoiceNumberText(invoice.number, invoice.numberSuffix)} от ${formatDate(invoice.date)}`}
      onClose={onClose}
      footer={
        <>
          {/*
            Кнопки документов — пилюлями: три действия одного рода стоят рядом,
            и скруглённая форма отделяет их от прямоугольных полей формы.
          */}
          {/*
            На телефоне «открыть в новой вкладке» не работает: установленное
            приложение их не создаёт, а мобильный Chrome не показывает PDF
            внутри себя. Там документ забирается и отдаётся как загрузка.
          */}
          {/*
            Печать берёт ТУ ЖЕ разметку, из которой собирается PDF, — значит
            распечатанное с экрана и сохранённое в файл совпадают до строки.
            Кнопка есть и на телефоне: там системное окно предложит принтер
            или «Сохранить как PDF».
          */}
          {model && (
            /*
              Акт печатается ДВУМЯ экземплярами одним заданием: его подписывают
              обе стороны, и каждой нужен свой. Счёт — одним.
            */
            <button onClick={() => printHtml(renderDocumentPage(model, { copies: model.kind === 'akt' ? 2 : 1 }))}>
              <Pill icon={Printer} label="Печать" />
            </button>
          )}

          {narrow ? (
            <>
              <button onClick={() => void grab('pdf')} disabled={busy !== null}>
                <Pill icon={FileText} label={busy === 'pdf' ? '…' : 'PDF'} primary />
              </button>
              <button onClick={() => void grab('docx')} disabled={busy !== null}>
                <Pill icon={Download} label={busy === 'docx' ? '…' : 'Word'} />
              </button>
            </>
          ) : (
            <>
              <a href={documentUrl(invoice.id, tab, 'pdf')} target="_blank" rel="noreferrer">
                <Pill icon={FileText} label="PDF" primary />
              </a>
              <a href={documentUrl(invoice.id, tab, 'docx')}>
                <Pill icon={Download} label="Word" />
              </a>
            </>
          )}
          {/*
            Статус «Отправлен» ставится по факту доставки письма, а не по
            нажатию (§2.9) — это решает сервер, а не эта кнопка.
          */}
          <button onClick={() => setSending(true)} disabled={!legal}>
            <Pill
              icon={Send}
              label="Отправить на почту"
              disabled={!legal}
              title={
                legal?.email?.trim()
                  ? `Счёт и акт уйдут на ${legal.email}`
                  : 'Укажите почту перед отправкой'
              }
            />
          </button>

          {/*
            Переход к заказам счёта.

            Счёт часто собран из заказов двух месяцев, и найти их в журнале
            вручную — значит перебрать оба месяца, помня, кого искать. Ссылка
            открывает журнал сразу нужной выборкой, без оглядки на месяц.
          */}
          {/*
            У второго счёта на ту же работу своих заказов нет — их забрал
            первый документ. Ссылка ведёт к его заказам: человеку нужен сам
            заказ, а не то, за каким он числится.
          */}
          <button
            /*
              СВОИ заказы всегда важнее связанного счёта.

              Ссылка между двумя счетами на одну работу двусторонняя, и если
              брать её без оглядки, у первого счёта кнопка вела бы к заказам
              второго — а у второго их нет. Поэтому связанный счёт берётся
              только тогда, когда своих заказов нет вовсе.
            */
            onClick={() => {
              /*
               * Карточка закрывается САМА, а не полагается на смену раздела.
               *
               * Из «Документов» переход уводил на другой раздел, тот исчезал
               * вместе с карточкой, и всё выглядело правильно. А из «Заказов»
               * адрес тот же — меняется только отбор, — раздел остаётся на
               * месте, и закрывать карточку было некому: заказы счёта
               * открывались ПОД ней, и она загораживала то, ради чего её
               * нажали.
               */
              onClose();
              navigate(
                `/orders?invoice=${invoice.orderCount > 0 ? invoice.id : invoice.linkedInvoiceId ?? invoice.id}`,
              );
            }}
            disabled={invoice.orderCount === 0 && !invoice.linkedInvoiceId}
          >
            <Pill
              icon={ListChecks}
              label="Заказы счёта"
              disabled={invoice.orderCount === 0 && !invoice.linkedInvoiceId}
              title={
                invoice.orderCount > 0
                  ? `Открыть ${invoice.orderCount} ${invoice.orderCount === 1 ? 'заказ' : 'заказов'} этого счёта в журнале`
                  : invoice.linkedInvoiceId
                    ? 'Открыть заказ, к которому относится этот счёт'
                    : 'У этого счёта нет заказов'
              }
            />
          </button>

          {changed && (
            <Button onClick={() => void persist()} disabled={saving}>
              {saving ? 'Сохранение…' : 'Сохранить правки'}
            </Button>
          )}

          <span className="ml-auto self-center text-sm text-ink2">
            Итого: <span className="tabular font-medium text-ink">{formatKop(totalKop)} ₽</span>
          </span>
        </>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <StatusPills
          invoice={invoice}
          email={legal?.email ?? ''}
          busy={payBusy}
          onTogglePaid={() => setConfirmPaid(!invoice.paid)}
        />
        <span className="text-sm text-ink">
          {clinic?.name} · {legal?.shortForm} «{legal?.name}»
        </span>
        <span className="text-xs text-ink2">заказов: {invoice.orderIds.length}</span>

        <button
          onClick={() => setEditMode((v) => !v)}
          className="ml-auto rounded-lg border px-3 py-1.5 text-sm"
          style={{
            borderColor: editMode ? 'var(--otc-accent)' : 'var(--otc-line)',
            color: editMode ? '#fff' : 'var(--otc-ink2)',
            /* На БЕЛОМ, как соседние кнопки: прозрачная сливалась с листом счёта. */
            background: editMode ? 'var(--otc-accent)' : 'var(--otc-surface)',
          }}
        >
          {editMode ? 'Готово' : 'Редактировать'}
        </button>
      </div>

      {editMode && (
        <div className="mb-4 rounded-lg border p-3" style={{ borderColor: 'var(--otc-line)' }}>
          <label className="mb-3 flex items-center gap-2 text-sm text-ink2">
            Номер счёта
            <input
              type="number"
              className="otc-input tabular w-24"
              value={number}
              onChange={(e) => setNumber(Number(e.target.value) || invoice.number)}
            />
            <span className="text-xs">Номер уникален в пределах года</span>
          </label>

          {!merged && (
            <div className="mb-3 flex flex-wrap items-center gap-4 text-sm">
              <ФамилияВСтроках
                label="ФИО пациента"
                checked={people?.patient ?? true}
                disabled={!invoiceOrders}
                onChange={(on) => switchPeople({ patient: on, doctor: people?.doctor ?? false })}
              />
              <ФамилияВСтроках
                label="ФИО врача"
                checked={people?.doctor ?? false}
                disabled={!invoiceOrders}
                onChange={(on) => switchPeople({ patient: people?.patient ?? true, doctor: on })}
              />
              <span className="text-[11px] text-ink2">
                {invoiceOrders
                  ? 'Меняются только строки, собранные по заказам'
                  : 'Заказы счёта загружаются…'}
              </span>
            </div>
          )}

          <table className="w-full text-xs">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--otc-line)' }}>
                <th className="px-2 py-1.5 text-left font-medium text-ink2">Наименование</th>
                <th className="w-16 px-2 py-1.5 text-right font-medium text-ink2">Кол-во</th>
                <th className="w-16 px-2 py-1.5 text-right font-medium text-ink2">Ед</th>
                <th className="w-28 px-2 py-1.5 text-right font-medium text-ink2">Цена, ₽</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={`${item.id}-${index}`} className="border-b" style={{ borderColor: 'var(--otc-line)' }}>
                  <td className="px-2 py-1">
                    <input
                      className="w-full rounded border px-1.5 py-1 text-xs"
                      style={{ borderColor: 'var(--otc-line)' }}
                      value={item.name}
                      onChange={(e) =>
                        setItems(items.map((it, i) => (i === index ? { ...it, name: e.target.value } : it)))
                      }
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      className="tabular w-full rounded border px-1.5 py-1 text-right text-xs"
                      style={{ borderColor: 'var(--otc-line)' }}
                      value={item.qty}
                      onChange={(e) =>
                        setItems(items.map((it, i) => (i === index ? { ...it, qty: Number(e.target.value) || 0 } : it)))
                      }
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      className="w-full rounded border px-1.5 py-1 text-right text-xs"
                      style={{ borderColor: 'var(--otc-line)' }}
                      value={item.unit}
                      onChange={(e) =>
                        setItems(items.map((it, i) => (i === index ? { ...it, unit: e.target.value } : it)))
                      }
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      className="tabular w-full rounded border px-1.5 py-1 text-right text-xs"
                      style={{ borderColor: 'var(--otc-line)' }}
                      value={formatKop(item.priceKop)}
                      onChange={(e) =>
                        setItems(
                          items.map((it, i) => (i === index ? { ...it, priceKop: rublesToKop(e.target.value) } : it)),
                        )
                      }
                    />
                  </td>
                  <td className="px-1 text-right">
                    <button
                      onClick={() => setItems(items.filter((_, i) => i !== index))}
                      className="rounded p-0.5 hover:bg-black/5"
                      aria-label="Удалить позицию"
                    >
                      <X size={12} style={{ color: 'var(--otc-coral)' }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button
            onClick={() =>
              setItems([...items, { id: `new_${items.length}`, name: 'Новая позиция', qty: 1, unit: '', priceKop: 0 }])
            }
            className="mt-2 inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs"
            style={{ borderColor: 'var(--otc-line)', color: 'var(--otc-accent)' }}
          >
            <Plus size={12} /> Добавить позицию
          </button>

          <p className="mt-2 text-[11px] text-ink2">
            Позиции счёта не связаны с заказами: правки здесь не меняют сами заказы.
          </p>
        </div>
      )}

      <div className="mb-2 flex flex-wrap items-center gap-1">
        {(['schet', 'akt'] as const).map((kind) => (
          <button
            key={kind}
            onClick={() => setTab(kind)}
            className="rounded-md border px-3 py-1.5 text-sm"
            style={{
              background: tab === kind ? 'var(--otc-accent-dark)' : 'var(--otc-surface)',
              color: tab === kind ? '#fff' : 'var(--otc-ink2)',
              borderColor: tab === kind ? 'var(--otc-accent-dark)' : 'var(--otc-line)',
            }}
          >
            {kind === 'schet' ? 'Счёт' : 'Акт'}
          </button>
        ))}

        {/*
          Факсимиле у каждого документа своё: счёт часто уходит с подписью и
          печатью, а акт клиника подписывает на бумаге — или наоборот.
        */}
        <label
          htmlFor="invoice-facsimile"
          className="ml-auto flex items-center gap-2 text-sm text-ink2"
          title={facsimileFiles || facsimileQuery.isLoading ? undefined : 'Файлы подписи и печати не загружены на сервер'}
        >
          <input
            id="invoice-facsimile"
            type="checkbox"
            checked={facsimileOn[tab]}
            disabled={facsimileBusy || (!facsimileFiles && !facsimileOn[tab])}
            onChange={() => void toggleFacsimile()}
          />
          Подпись и печать в {tab === 'schet' ? 'счёте' : 'акте'}
        </label>
      </div>

      {model ? (
        <DocumentPreview model={model} />
      ) : (
        <p className="pb-4 text-sm" style={{ color: 'var(--otc-coral)' }}>
          Не заданы реквизиты исполнителя — заполните их в «Документы → Реквизиты».
        </p>
      )}

      {error && (
        <p className="pb-3 text-sm" style={{ color: 'var(--otc-coral)' }} role="alert">
          {error}
        </p>
      )}

      {/*
        Удаление — в правом нижнем углу и красной кнопкой. Прежде это была
        неприметная строчка посреди окна, вплотную к полям правки позиций:
        промах по ней стоил счёта.
      */}
      <div className="flex justify-end pb-2 pt-1">
        <button
          onClick={() => setConfirmDelete(true)}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          style={{ background: 'var(--otc-coral)' }}
        >
          <Trash2 size={14} /> Удалить счёт и акт
        </button>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title={`Удалить счёт и акт №${invoiceNumberText(invoice.number, invoice.numberSuffix)}?`}
          description="Документы исчезнут из истории, а заказы вернутся в свободные — их можно будет включить в другой счёт."
          details={
            <p>
              Позиций: {items.length} · заказов: {invoice.orderIds.length} ·{' '}
              {formatKop(totalKop)} ₽
            </p>
          }
          confirmLabel={saving ? 'Удаление…' : 'Удалить счёт и акт'}
          onConfirm={() => void remove()}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      {/*
        Оплата подтверждается, потому что действие МАССОВОЕ: одним нажатием
        меняются все заказы счёта, а в журнале это будет выглядеть как четырнадцать
        правок подряд. Окно называет число заказов — на нём и видно, что счёт
        выбран тот самый.
      */}
      {confirmPaid !== null && (
        <ConfirmDialog
          title={
            confirmPaid
              ? `Отметить счёт №${invoiceNumberText(invoice.number, invoice.numberSuffix)} оплаченным?`
              : `Снять оплату со счёта №${invoiceNumberText(invoice.number, invoice.numberSuffix)}?`
          }
          description={
            confirmPaid
              ? 'Все заказы этого счёта станут оплаченными — и в журнале заказов, и в финансах.'
              : 'Со всех заказов этого счёта будет снята отметка оплаты. Счёт снова станет неоплаченным.'
          }
          details={
            <p>
              {clinic?.name} · {legal?.name}
              <span className="mt-1 block text-xs text-ink2">
                Заказов: {invoice.orderCount}
                {invoice.paidCount > 0 && !invoice.paid
                  ? ` · из них уже оплачено ${invoice.paidCount}`
                  : ''}{' '}
                · {formatKop(totalKop)} ₽
              </span>
            </p>
          }
          confirmLabel={
            payBusy ? 'Сохранение…' : confirmPaid ? 'Отметить оплаченным' : 'Снять оплату'
          }
          /* Оплата — не удаление: ничего не пропадает, и всё делается обратно. */
          tone="accent"
          onConfirm={() => void setPaid(confirmPaid)}
          onCancel={() => setConfirmPaid(null)}
        />
      )}

      {/*
        Подтверждение перед отправкой — то же окно, что у писем врачу. Счёт
        уходит бухгалтерии клиники, и увидеть, что именно уйдёт, нужно тем
        более. Окно поверх счёта, а не вместо него: закрыв его, остаёшься там,
        откуда пришёл.
      */}
      {sending && legal && (
        <MailComposeDialog
          title={`Отправить документы № ${invoiceNumberText(invoice.number, invoice.numberSuffix)} в «${legal.name}»?`}
          recipientName={legal.name}
          toEmail={legal.email ?? ''}
          letter={mailLetter}
          attachments={mailDocuments.map(kind => `${kind==='schet'?'Счет':'Акт'} №${invoice.number} ${legal.name}.docx`)}
          sendDisabled={!mailDocuments.length}
          children={<fieldset className="mb-3 flex flex-wrap gap-4 text-sm"><legend className="mb-2 text-xs text-ink2">Что отправить</legend>{(['schet','akt'] as const).map(kind=><label key={kind} className="flex items-center gap-2"><input type="checkbox" checked={mailDocuments.includes(kind)} onChange={e=>setMailDocuments(old=>e.target.checked?[...old,kind]:old.filter(k=>k!==kind))}/>{kind==='schet'?'Счёт':'Акт'}</label>)}</fieldset>}
          note="Текст письма правится в «Документы → Шаблоны»."
          onCancel={() => setSending(false)}
          onSend={async ({ subject, body, toEmail }) => {
            const result = await api.post<{ sentTo: string }>(
              `/api/invoices/${invoice.id}/send`,
              { subject, body, toEmail, documents: mailDocuments },
            );
            invalidateAfterCommit(queryClient);
            setSending(false);
            onChanged(`Письмо отправлено на ${result.sentTo}`, true);
          }}
        />
      )}
    </CardWindow>
  );
}

/**
 * Кнопка-пилюля для действий с документом.
 *
 * Скруглённая форма отделяет действия («получить файл», «отправить») от
 * прямоугольных полей правки позиций, стоящих выше в том же окне.
 *
 * Выключенная кнопка остаётся ВИДНОЙ, а не прячется: она сообщает, что такое
 * действие в программе есть и появится, — иначе его отсутствие читается как
 * недоделка, о которой не предупредили.
 */
function Pill({
  icon: Icon,
  label,
  primary,
  disabled,
  title,
}: {
  icon: (props: { size?: number | string }) => ReactNode;
  label: string;
  primary?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      disabled={disabled}
      title={title}
      className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-45"
      style={{
        background: primary ? 'var(--otc-accent-dark)' : 'var(--otc-surface)',
        color: primary ? '#fff' : 'var(--otc-ink)',
        border: `1px solid ${primary ? 'var(--otc-accent-dark)' : 'var(--otc-line)'}`,
      }}
    >
      <Icon size={14} /> {label}
    </button>
  );
}

/**
 * Отправка и оплата — ДВЕ метки, а не одна.
 *
 * Раньше здесь стояла одна пилюля со сводным статусом, и «Оплачено» заслоняло
 * собой отправку: по счёту нельзя было понять, ушло ли письмо в клинику, —
 * а это разные события и разные вопросы. Оплаченный счёт бывает неотправленным
 * (заплатили по бумажной копии), отправленный — неоплаченным месяцами.
 *
 * Отправка ставится сервером по факту доставки письма (§2.9) и вручную не
 * назначается. Оплата, наоборот, отмечается вручную — деньги приходят на счёт,
 * а не в программу, и узнать об этом можно только от человека.
 */
function StatusPills({
  invoice,
  email,
  onTogglePaid,
  busy,
}: {
  invoice: ApiInvoice;
  email: string;
  onTogglePaid: () => void;
  busy: boolean;
}) {
  const отправка = invoice.sent
    ? {
        color: 'var(--otc-accent)',
        icon: Send,
        label: 'Отправлен',
        hint: email ? `Письмо ушло на ${email}` : 'Письмо отправлено',
      }
    : {
        color: STATUS_COLORS.danger,
        icon: Mail,
        label: 'Не отправлен',
        hint: email ? `Письмо ещё не уходило · ${email}` : 'У юрлица не указана почта для счетов',
      };

  /*
   * Частичная оплата показывается числом.
   *
   * «Не оплачен» на счёте, где из четырнадцати заказов оплачено тринадцать, —
   * формально правда, но по ней не видно, что осталась одна работа. Число
   * сразу говорит, сколько ещё ждать и стоит ли вообще звонить в клинику.
   */
  const частично = invoice.paidCount > 0 && !invoice.paid;
  /*
   * «Не оплачен» — КРАСНЫМ, как «Не отправлен» рядом.
   *
   * Серым это читалось как «ничего особенного», хотя неоплаченный счёт —
   * это долг клиники, то самое, ради чего в счёт и заглядывают. Частичная
   * оплата остаётся жёлтой: деньги идут, вопрос только в остатке.
   */
  const оплата = invoice.paid
    ? { color: STATUS_COLORS.done, icon: CheckCircle2, label: 'Оплачен', hint: 'Оплачены все заказы счёта' }
    : {
        color: частично ? 'var(--otc-amber, #AE5E1E)' : STATUS_COLORS.danger,
        icon: Wallet,
        label: 'Не оплачен',
        hint: частично
          ? `Оплачено ${invoice.paidCount} из ${invoice.orderCount} заказов`
          : invoice.orderCount === 0
            ? 'Отметка ставится вручную: у счёта нет своих заказов'
            : `Заказов в счёте: ${invoice.orderCount}`,
      };


  return (
    <span className="flex flex-wrap items-center gap-2">
      <Pilule look={отправка} />
      <Pilule look={оплата} />

      {/*
        Оплата отмечается ЗДЕСЬ, а не по заказам поштучно.

        Раньше приходилось открывать журнал, находить все строки этого счёта и
        щёлкать каждую: на счёте из четырнадцати заказов — четырнадцать шансов
        пропустить одну и потом не понять, почему счёт всё ещё висит.

        Кнопка меняет именно заказы: оплата хранится на них, а счёт её лишь
        показывает. Поэтому обратный ход равноценен прямому — в выписке
        ошибаются, платёж отзывают, деньги приходят от другой клиники.
      */}
      {/*
        Кнопка — ТОЙ ЖЕ формы, что метки слева, но БЕЛАЯ.

        Раньше она стояла голым текстом и читалась как подпись, а не как
        действие. Потом стала зелёной пилюлей — и начала спорить с меткой
        рядом: два цветных пятна в ряду, и непонятно, где состояние, а где
        действие. Белая с тонкой рамкой оставляет цвет меткам.
      */}
      <ActionPill
        icon={invoice.paid ? Wallet : CheckCircle2}
        onClick={onTogglePaid}
        disabled={busy}
        title={
          invoice.paid
            ? 'Снять отметку оплаты со всех заказов счёта'
            : 'Отметить оплаченными все заказы счёта'
        }
      >
        {busy ? '…' : invoice.paid ? 'Снять оплату' : 'Отметить оплаченным'}
      </ActionPill>
    </span>
  );
}



/**
 * Сводится ли этот счёт в одну строку.
 *
 * У таких юрлиц фамилий в счёте нет по определению: за строкой стоит месяц
 * работы, а не один пациент. Выключатели там показывать нечего.
 */
function legalMerged(bootstrap: Bootstrap, invoice: ApiInvoice): boolean {
  const clinic = bootstrap.clinics.find((c) => c.id === invoice.clinicId);
  const legal = clinic?.legals.find((l) => l.id === invoice.legalId);
  return legal?.mergeInvoiceItems === true;
}

/** Галочка фамилии в строках счёта. */
function ФамилияВСтроках({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-ink2">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}
